import { ChangeEvent, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { Field } from "@atlaskit/form";
import TextField from "@atlaskit/textfield";
import { token } from "@atlaskit/tokens";

import { emailIsValid, fontSize } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { useAccountCreationContext } from "../../hooks";
import { InputIcon } from "../input-icon";

const HELP_TEXT_ID = "account_creation_email_help_text";

export const Input = ({
    helpText,
    errorMessageText,
    labelText,
    placeholderText
}: {
    helpText?: string | null;
    errorMessageText?: string;
    labelText?: string;
    placeholderText?: string;
}) => {
    const {
        isReadyForInvalid,
        setIsEmailValid,
        setIsReadyForInvalid,
        setTypeAheadEmail,
        typeAheadEmail
    } = useAccountCreationContext();
    const [email, setEmail] = useState(typeAheadEmail);
    const [isValid, setValid] = useState<boolean>();
    const intl = useIntl();

    if (helpText === undefined) {
        helpText = intl.formatMessage({
            id: "signupForm.accountCreation.helperText",
            defaultMessage:
                "Using a work email helps find teammates and boost collaboration."
        });
    }
    if (errorMessageText === undefined) {
        errorMessageText = intl.formatMessage({
            id: "BxpUiSignup.SignupForm.invalidEmail",
            defaultMessage: "Please enter a valid email address."
        });
    }
    if (labelText === undefined) {
        labelText = intl.formatMessage({
            id: "BxpUiSignup.SignupForm.workEmail",
            defaultMessage: "Work email"
        });
    }
    if (placeholderText === undefined) {
        placeholderText = intl.formatMessage({
            id: "BxpUiSignup.SignupForm.emailPlaceholder",
            defaultMessage: "you@company.com"
        });
    }

    useEffect(() => {
        const isValid = emailIsValid(email);

        // wait to show invalid state if the user is not finished typing
        if (!isValid && !isReadyForInvalid) {
            setValid(undefined);
            return;
        }

        setValid(isValid);
        setIsEmailValid(isValid);
    }, [email, setIsEmailValid, isReadyForInvalid]);

    const team26VisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );

    return (
        <div css={[fieldCss, team26VisualUpdates && fieldV2Css]}>
            <Field
                aria-required={true}
                isRequired
                label={labelText}
                name="email"
            >
                {({ fieldProps }) => (
                    <div
                        css={[
                            inputCss,
                            isValid === true && inputValidCss,
                            isValid === false && inputInvalidCss
                        ]}
                    >
                        <TextField
                            {...fieldProps}
                            autoComplete="email"
                            aria-describedby={HELP_TEXT_ID}
                            elemAfterInput={<InputIcon valid={isValid} />}
                            onBlur={() => setIsReadyForInvalid(true)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setEmail(e.target.value);
                                setTypeAheadEmail(e.target.value);
                                fieldProps.onChange(e);
                            }}
                            placeholder={placeholderText}
                            testId="email_field"
                            type="email"
                            value={email}
                        />
                        {isValid === false ? (
                            <div
                                css={errorMessageCss}
                                id={HELP_TEXT_ID}
                                role="alert"
                            >
                                {errorMessageText}
                            </div>
                        ) : helpText ? (
                            <div css={helpTextCss} id={HELP_TEXT_ID}>
                                {helpText}
                            </div>
                        ) : null}
                    </div>
                )}
            </Field>
        </div>
    );
};

const errorMessageCss = css({
    color: token("color.text.danger"),
    fontSize: fontSize.xs,
    lineHeight: 1.5,
    marginBlockStart: 6
});

const helpTextCss = css({
    fontSize: fontSize.xs,
    lineHeight: 1.5,
    opacity: 0.8,
    marginBlockStart: 6
});

const fieldCss = css({
    marginBlockEnd: 16,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    input: {
        backgroundColor: `${wacToken("color.neutral.light0")} !important`,
        fontFamily: wacToken("font.charlie.text"),
        fontSize: fontSize.md,
        paddingInlineStart: "12px !important"
    },
    ["&:first-of-type"]: {
        marginBlockStart: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    label: {
        color: "inherit",
        fontFamily: wacToken("font.charlie.text"),
        fontWeight: 500,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- hide asterisk
        ["[aria-hidden=true]"]: {
            display: "none"
        }
    }
});

// todo remove !important when we productionize gate and merge properties with fieldCss
const fieldV2Css = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    label: {
        fontFamily: `inherit !important`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    input: {
        fontFamily: `inherit !important`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div[data-ds--text-field--container]"]: {
        borderWidth: token("border.width", "1px")
    }
});

const inputCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div[data-ds--text-field--container]"]: {
        borderRadius: 21
    }
});

const inputInvalidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div[data-ds--text-field--container]"]: {
        borderColor: `${token("color.border.danger")} !important`,
        borderWidth: token("border.width.focused", "2px")
    }
});

const inputValidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div[data-ds--text-field--container]"]: {
        borderColor: `${token("color.border.success")} !important`,
        borderWidth: token("border.width.focused", "2px")
    }
});
